// #region: Class definition *********************************************************************/
/**
 * @class PartIndexMenu
 * @description Custom web component that provides a 'jump' menu to navigate the available parts in the page.
 * 
 * @extends DropDown
 * Extends the DropDown web component.
 *
 * @example
 * <drop-down class="part-index-menu" icon="arrow-icon" label="Jump to...">
 *    <div class="drop-down-content">
 *        <ul class="part-index-list"></ul>
 *    </div>
 * </drop-down>
 */
 
class PartIndexMenu extends DropDown {

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of PartIndexMenu.
     */
    constructor() {
        super();

        // Add a label in the drop down for when button label is hidden.
        const buttonLabel = this.querySelector('.button-label');
        const menuContent = this.querySelector('.drop-down-content');
        const label = document.createElement('span');

        label.classList.add('part-list-label');
        label.textContent = buttonLabel.textContent;

        menuContent.insertBefore(label, menuContent.firstChild);
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof PartIndexMenu
     */
    static tagName = 'part-index-menu';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     * 
     * @memberof PartIndexMenu
     */
    toString() {
        return '[PartIndexMenu]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     * @memberof PartIndexMenu
     */
    static toString() {
        return '[PartIndexMenu]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     * @memberof PartIndexMenu
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when the custom element is attached to the DOM.
     * 
     * @memberof PartIndexMenu
     */
    connectedCallback() {
        super.connectedCallback();

        // When a part link is clicked.
        this.addEventListener('click', this.handleClick, { capture: false });
    }

    /**
     * Cleanup. Called when the component is removed from the DOM.
     * 
     * @memberof PartIndexMenu
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener('click', this.handleClick);
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles click events on menu items.
     * @param {Event} e The click event data.
     * 
     * @memberof PartIndexMenu
     */
    handleClick(e) {
        const link = e.target.closest('a');

        if (link) {
            // Handle click event for links.
            e.preventDefault();

            const partId = link.getAttribute('href');
            const part = document.querySelector(partId);
            const title = part.querySelector('.form-part-title');

            title.classList.remove('scroll-reached');

            /* eslint-disable no-magic-numbers -- This for animation. */
            this.#scrollToElement(part, 300);

            /* eslint-disable no-magic-numbers -- This for animation. */
            setTimeout(() => {
                title.classList.add('scroll-reached');
            }, 200);
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Initialize the UI (on 'DOMContentLoaded').
     * 
     * @memberof PartIndexMenu
     */
    initialize() {
        super.initialize();

        this.loadPartIndex();
    }

    /**
     * Loads the part index by creating navigation links from form parts.
     * Removes existing items and generates new ones based on visible form parts.
     * 
     * @memberof PartIndexMenu
     */
    loadPartIndex() {
        // Get the list element.
        const list = document.querySelector('.part-index-list');

        // Remove all child elements.
        [...list.children].forEach((child) => {
            child.remove();

            return;
        });

        // Process all form part containers.
        document.querySelectorAll('.form-part-container').forEach((part) => {
            // Skip if the part is hidden.
            if (part.classList.contains('hidden') ||
                part.classList.contains('js-index-hidden')) {
                return;
            }

            // Get the part title and ID.
            const partTitle = part.querySelector('.form-part-title').textContent;
            const partId = part.id;
            
            // Get part number if available (when question numbers are enabled).
            const partNumber = part.dataset.partNumber;
            const displayText = partNumber ? `${partNumber}. ${partTitle}` : partTitle;

            // Create anchor element.
            const anchor = document.createElement('a');
            anchor.href = `#${partId}`;
            anchor.textContent = displayText;

            // Create list item and append anchor.
            const listItem = document.createElement('li');
            listItem.appendChild(anchor);

            // Append to the main list.
            list.appendChild(listItem);
        });

        // Setup accessibility attributes.
        this.setupAriaAttributes();
    }

    /**
     * Toggles the visibility of the submenu.
     * 
     * @memberof PartIndexMenu
     */
    toggleMenu() {
        if (!this.isActive) {
            // Update part index first.
            this.loadPartIndex();
        }

        super.toggleMenu();
    }

    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof PartIndexMenu
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Scrolls the viewport to the specified element.
     * @param {HTMLElement} element The target element to scroll to.
     * @param {number} animationDuration Duration of scroll animation in milliseconds. If 0, scrolls instantly.
     * 
     * @private
     * @memberof PartIndexMenu
     */
    #scrollToElement(element, animationDuration) {
        // Get the element's position relative to the viewport.
        const elementRect = element.getBoundingClientRect();
        const destinationOffset = elementRect.top + window.pageYOffset;

        // Get the fixed header height.
        const header = document.querySelector('.js-fixed-header');
        const headerHeight = header ? header.offsetHeight : 0;

        // Get computed margin-bottom of the element.
        const computedStyle = window.getComputedStyle(element);
        const margin = parseInt(computedStyle.marginBottom) || 0;

        // Calculate the final scroll position.
        const newScrollTop = destinationOffset - headerHeight - margin;

        if (animationDuration > 0) {
            // Smooth scroll with animation.
            window.scrollTo({ behavior: 'smooth', top: newScrollTop });
        }
        else {
            // Instant scroll.
            window.scrollTo({ top: newScrollTop });
        }
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register custom element.
 * Defines the custom element 'part-index-menu' and maps it to the PartIndexMenu class.
 */
customElements.define(PartIndexMenu.tagName, PartIndexMenu);
